// ─── Server-truth gate for print originals ───────────────────────────────────
// THE fix for the silent-loss bug: before submit / PDF generation, the client
// must ask the SERVER whether every photo's full-res original physically exists
// in storage (byte-check) — never trust the tab's in-memory upload tracker.
// After a refresh that tracker is EMPTY, and the old gate treated "nothing
// tracked" as "all good", letting orders through with thumbnail-only photos.
//
// Contract:
//   - ok:true            → the server checked every photo and found bytes for all.
//   - ok:false + missing → these photos have NO original in storage; the caller
//                          must block and show exactly which.
//   - ok:false + unavailable → the server couldn't be reached / answered
//                          garbage. FAIL CLOSED: block with a "réessayez"
//                          message. A connectivity blip must never become a
//                          false "all good".

export interface MissingOriginal {
  photo_id: string;
  original_filename: string | null;
  /** true → a display thumbnail exists (the tile LOOKS fine but isn't printable). */
  has_thumbnail: boolean;
}

export interface OriginalsStatus {
  checked: number;
  missing: MissingOriginal[];
}

export interface GateResult {
  ok: boolean;
  missing: MissingOriginal[];
  /** true → verdict is "couldn't verify" (server unreachable), NOT "verified missing". */
  unavailable: boolean;
}

export type StatusFetcher = () => Promise<OriginalsStatus>;

/** Fix 2 — a photo may only be marked "done" after the SERVER confirms its
 *  original's bytes are in storage (GET /photos/:id/verify → { ok }). Polls a
 *  few times to ride out storage list-consistency lag after an upload; a
 *  thrown fetch error counts as not-confirmed-yet and keeps polling. Returns
 *  false when the bytes never show — the caller must treat the upload as
 *  FAILED (keep the source bytes, retry later), never as done. */
export async function confirmOriginalLanded(
  verify: () => Promise<{ ok: boolean; size?: number }>,
  opts?: { attempts?: number; backoffMs?: number },
): Promise<boolean> {
  const attempts = opts?.attempts ?? 4;
  const backoffMs = opts?.backoffMs ?? 1000;
  for (let i = 0; i < attempts; i++) {
    if (i > 0 && backoffMs > 0) await new Promise((r) => setTimeout(r, backoffMs * i));
    try {
      const res = await verify();
      if (res?.ok) return true;
    } catch { /* not a verdict — poll again */ }
  }
  return false;
}

/** Customer-facing (FR) message for a blocked submit: name the exact photos
 *  whose print original is missing, or a retry hint when the server couldn't
 *  be reached (missing list empty). */
export function describeMissingOriginals(missing: MissingOriginal[]): string {
  if (missing.length === 0) {
    return "Impossible de vérifier vos photos pour le moment. Vérifiez votre connexion et réessayez.";
  }
  const named = missing
    .map((m) => m.original_filename)
    .filter((n): n is string => !!n)
    .slice(0, 8);
  const head = `${missing.length} photo${missing.length > 1 ? 's' : ''} n'${missing.length > 1 ? 'ont' : 'a'} pas été reçue${missing.length > 1 ? 's' : ''} en pleine qualité — à re-téléverser avant de continuer`;
  const list = named.length > 0
    ? ` :\n• ${named.join('\n• ')}${missing.length > named.length ? `\n… et ${missing.length - named.length} autre${missing.length - named.length > 1 ? 's' : ''}` : ''}`
    : '.';
  return head + list;
}

export async function serverVerifyOriginals(
  fetchStatus: StatusFetcher,
  opts?: { retries?: number; backoffMs?: number },
): Promise<GateResult> {
  const retries = opts?.retries ?? 2;
  const backoffMs = opts?.backoffMs ?? 1500;

  for (let attempt = 0; attempt <= retries; attempt++) {
    if (attempt > 0 && backoffMs > 0) {
      await new Promise((r) => setTimeout(r, backoffMs * attempt));
    }
    try {
      const status = await fetchStatus();
      // Malformed body (proxy error page, partial JSON…) → not a verdict.
      if (!status || typeof status.checked !== 'number' || !Array.isArray(status.missing)) {
        continue;
      }
      return { ok: status.missing.length === 0, missing: status.missing, unavailable: false };
    } catch {
      // transient — retry
    }
  }
  return { ok: false, missing: [], unavailable: true };
}
